<template>
  <v-container :key="reloadKey">
    <HandleImportError
      :dialog="dialog"
      :errorMode="errorMode"
      :errors="errors"
      :showDependencyBtn="showDependencyBtn"
      @openCloseDialog="openCloseDialog"
      @getAdditionalImportedData="getAdditionalImportedData"
    />
    <v-row class="mt-0 pt-0">
      <v-col cols="6">
        <!-- Deliberately NOT gated on the `disabled` prop: that prop means "the screen is
             not ready to import", and on Bill Requests part of being ready IS having
             chosen a file here. Disabling this on it locks the user out of the only
             control that could satisfy the gate. -->
        <v-file-input :disabled="!pipeline"
        v-model="file"
        :accept="acceptedFormats"
        :label="'Import ' + acceptedFormats"
        show-size
        :rules="checkSize"
        clearable
        class="input-text-size" />
      </v-col>
      <v-col cols="6">
        <!-- Segmented so the clerk can see both states. isBackupNeeded reads backwards -
             true means the run skips the dump - but the labels have always told the truth, so
             they are kept exactly as they were. -->
        <BinaryToggle
          id="tmc-transform-import-isBackupNeeded"
          aria-label="Database backup before import: on or off"
          off-label="Backup ON"
          on-label="Backup OFF"
          v-model="isBackupNeeded" />
      </v-col>
    </v-row>
    <div class="d-flex align-center justify-space-around flex-row
        taxroll-table-wrapper-two">
      <v-btn class="input-text-size" text color="blue darken-1"
       @click="importFile" :loading="isImportRunning"
       :disabled="disableButtons || disabled || resolvingFile || isImportRunning">
        {{ dynamicNameForImport }}
      </v-btn>
      <!-- Deliberately NOT gated on `disableButtons`: that flag is also raised by the
           30 MB size rule, and a file too big to import is exactly the one a user wants
           to look at. Only a fetch already in flight closes this button. -->
      <v-btn class="input-text-size" text color="blue darken-1" @click="viewFile"
       :disabled="resolvingFile">
        View File
      </v-btn>
    </div>
    <v-row v-if="importing">
      <v-col cols="2"></v-col>
      <v-col cols="8">
        <p class="input-text-size">{{statusText}}</p>
          <v-progress-linear
          color="deep-purple accent-4"
          :value="progress"
          rounded
          height="5"
          ></v-progress-linear>
      </v-col>
    </v-row>
    <v-row v-if="reload">
      <v-col cols="12">
        <v-btn class="input-text-size" rounded
          id="tmc-btn-import-state-reload-beta" @click="reloadMe">
          <v-icon class="mr-2">{{icons.mdiReload}}</v-icon> Load another
        </v-btn>
      </v-col>
      <v-col cols="12">
        <a v-if="hasReportTemplate"
          class="input-text-size"
          :href="dynamicReportUrl()"
          target="_blank">Open the report</a>
      </v-col>
      <v-col cols="12">
        <span class="input-text-size" v-if="showImportTimmings">Imported in: {{this.timing}}</span>
      </v-col>
    </v-row>
    <CRUDDialog ref="crudDialog" :baseCrudKey="baseCrudKey"
  dialogDynamicWidth="100vw"
  :canSave="false">
    <template v-slot:[`editedItem`]="{ }">
      <v-textarea id="edited-item-remarks"
          class="war-trans-item-remarks"
          rows="10"
          :value="dataFromImport"
          ></v-textarea>
    </template>
      </CRUDDialog>
  </v-container>
</template>

<script>
import {
  mdiReload,
} from '@mdi/js';
import {
  mapActions,
  mapMutations,
  mapState,
} from 'vuex';
import { clients } from '../../../util/clients';
import {
  dateDiffStr,
} from '../../../util/shared/tmc-global';
import {
  amountToNumber,
  sanitizeFilename,
} from '../../../util/shared/vue-global';
import {
  generateReportUrl,
  generateChunckID,
  getImportRedirectionUrl,
} from '../../config/TransformMapper.util';
import BinaryToggle from '../base/BinaryToggle.vue';
import HandleImportError from '../../../views/bulk/HandleImportError.vue';
import { filePreValidate, importBatchErrorMessage, importFileProgramMapperWithBackup } from '../../../views/bulk/import.utils';
// import { filePreValidate, importFileProgramMapperWithBackup } from './import.utils';
import CRUDDialog from '../base/crud-2.0/CRUDDialog.vue';
import '../../../assets/style.css';

const { backendRest } = clients.direct;

const initdata = () => ({
  form: new FormData(),
  dialog: false,
  errorMode: undefined,
  icons: {
    mdiReload,
  },
  file: undefined,
  importing: false,
  reload: false,
  reloadKey: 0,
  statusText: '',
  progress: 0,
  timing: undefined,
  errors: [],
  chunkID: '',
  additionalImportedData: {},
  programSelect: undefined,
  baseCrudKey: 'commonImportShowFile',
  dataFromImport: null,
  disableButtons: false,
  // A fileResolver() call is in flight. Separate from `disableButtons`, which doubles
  // as the oversize-file flag and so cannot be reused for "busy" without closing the
  // buttons on files that are merely too big to import.
  resolvingFile: false,
  // An Import run is in flight, from the click until the upload returns. Also separate from
  // `disableButtons`, which the file-size rule rewrites on every validation.
  isImportRunning: false,
  statusPolling: null,
  isBackupNeeded: false,
});

export default {
  name: 'CommonImportFiles',
  components: {
    BinaryToggle,
    HandleImportError,
    CRUDDialog,
  },
  data: () => initdata(),
  props: {
    acceptedFormats: {
      type: String,
      default: '.txt,.csv,.prn',
    },
    profileName: {
      type: String,
      default: '',
    },
    dynamicNameForImport: {
      type: String,
      default: 'Import',
    },
    // One constant entry ({ type, prop, value }) stamped onto every imported row, or an
    // ARRAY of them when a screen needs more than one. Bill Requests passes an array: it
    // stamps both the chosen tax year and the chosen Company, and the Company is the only
    // thing naming the mortgage company now that the profiles are company-neutral.
    dynamicUpdateInputs: {
      type: [Object, Array],
      default: () => {},
    },
    // Holds the Import button closed while the screen is still missing a required
    // selection. The screen owns that rule - this component cannot know which dropdowns
    // matter. It does NOT reach the file input; see the comment on it.
    disabled: {
      type: Boolean,
      default: false,
    },
    showImportTimmings: {
      type: Boolean,
      default: true,
    },
    makeBackupToTrue: {
      type: Boolean,
      default: false,
    },
    // Optional async source for the file, used when nothing has been picked with
    // the file input. Bill Requests supplies one that pulls the chosen file off the
    // FTP server. Called at Import time only, and the File it returns goes through
    // exactly the same pre-validate / backup / upload path as a local pick, so
    // there is no second import route to keep in step with this one.
    // Returning null means "nothing to import" and Import quietly does nothing.
    fileResolver: {
      type: Function,
      default: null,
    },
  },
  computed: {
    ...mapState({
      warSettingItem: (state) => state.SystemConfig.warSettingItem,
      pipeline: (state) => state.tax.MortgageCoProcessing.pipeline,
    }),
    showDependencyBtn() {
      return (this.pipeline || {}).createDependency || false;
    },
    checkSize() {
      return [
        (value) => {
          if (value && value.size > 30000000) {
            this.disableButtons = true;
            return 'File size should be less than 30 MB';
          }
          this.disableButtons = false;
          return true;
        },
      ];
    },
  },
  created() {
    this.loadConfig({ requiredScope: 'warconfig' });
    this.loadConfig({ requiredScope: 'global' });
  },
  mounted() {
    this.isBackupNeeded = this.makeBackupToTrue;
  },
  methods: {
    ...mapActions('SystemConfig', ['loadConfig']),
    ...mapActions('jobs', [
      'backup',
    ]),
    ...mapMutations('jobs', [
      'setPrefix',
      'setGroup',
      'setCollection',
    ]),
    ...mapActions([
      'flashError',
      'flashInfo',
      'flashSuccess',
    ]),
    ...mapMutations('base/crud', [
      'setIsVisible',
    ]),
    handleDialog() {
      this.setIsVisible([this.baseCrudKey, true]);
    },
    getAdditionalImportedData(val) {
      this.additionalImportedData = { ...val };
    },
    openCloseDialog(value) {
      this.dialog = value;
    },
    // async onFileSelected(file) {
    //   console.info(file);
    //   if (file) {
    //     await this.importFile(file);
    //   }
    // },
    async insertUploadedFile(form) {
      this.importing = true;
      try {
        form.append('preValidate', false);
        const formHeaders = {
          'Content-Type': 'multipart/form-data',
        };
        const results = await backendRest.post('/transmap/import', form, {
          headers: formHeaders,
          onUploadProgress: (progEvt) => {
            console.info('upload');
            try {
              const percentComplete = (progEvt.loaded / progEvt.total) * 100;
              this.statusText = `${percentComplete}% of file uploaded...`;
              this.progress = percentComplete;
            } catch {
              // do nothing
            }
          },
          onDownloadProgress: (progEvt) => {
            console.info('download');
            try {
              const kbRecvd = progEvt.loaded / 1024;
              this.statusText = `Getting import progress (${amountToNumber(kbRecvd)} kb)...`;
              this.progress = kbRecvd;
            } catch {
              // do nothing
            }
          },
        });
        this.importing = false;
        this.statusText = '';
        this.file = undefined;

        const startDt = this.timing;
        const endDt = new Date();
        this.timing = dateDiffStr(startDt, endDt);

        this.reload = true;
        if (!results || !results.data || results.data.ok !== 1) {
          // Say which batch failed and how much of the file is missing, when the server
          // sent that - 'Unable to upload file' reads as 'nothing happened', and the rows
          // that did land are still sitting in the staging collection.
          this.flashError(importBatchErrorMessage(results) || 'Unable to upload file.');
          this.$emit('loadCRUDList');
          return false;
        }
        this.$emit('loadCRUDList');
        this.flashSuccess('Imported!');

        this.form = new FormData();
        if (!(this.errors == null) && this.errors.length > 0) {
          this.$router.push({
            name: 'Auto Created Dependencies',
            params: {
              args: this.errors,
              redirectToPage: {
                url: this.dynamicReportUrl(),
                params: this.chunkID,
              },
            },
          });
        }
        // else {
        //   this.openIfReportTemplate();
        // }
        return true;
      } catch (err) {
        console.error('Error while upserting imported file', err);
        // Used to fall through to `return true` with the spinner still running, so a
        // request that never completed looked exactly like one that did.
        this.importing = false;
        this.statusText = '';
        this.flashError('There was a problem while importing your file. Check the results '
          + 'before relying on this import, or import the file again.');
      }
      return false;
    },
    // The first 1 MB of the file, as text, resolved before anyone is shown anything.
    // One reader over one slice: the previous version started a reader per 2,000-byte
    // chunk and pushed each result from its own async 'load' callback, so chunks could
    // land out of order - and the dialog opened immediately, before most of them had
    // landed at all.
    readFileHead(file) {
      const bytesVal = 1000000; // its equal to 1mb
      const blob = file.slice(0, Math.min(file.size, bytesVal));
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => resolve(reader.result || ''), false);
        reader.addEventListener('error', () => {
          console.error('Error while reading file for view', reader.error);
          resolve('');
        }, false);
        reader.readAsText(blob);
      });
    },
    async viewFile() {
      // The same fallback Import uses: a locally picked file wins, otherwise ask the
      // parent for one. Without this View File was dead on Bill Requests for a file
      // chosen off the FTP list, because nothing there ever fills the file input.
      let { file } = this;
      if (!file && this.fileResolver) {
        this.resolvingFile = true;
        try {
          file = await this.fileResolver();
        } finally {
          this.resolvingFile = false;
        }
      }

      // Nothing picked locally and nothing the resolver could supply - said out loud
      // for the same reason Import says it: a button that does nothing at all cannot be
      // told apart from a broken one.
      if (!file) {
        this.flashInfo('Please choose a file to view, or select one from the file list.');
        return false;
      }

      this.dataFromImport = await this.readFileHead(file);
      this.handleDialog();
      return true;
    },
    // The constant entries stamped onto every row of the file, sent as the `inputs`
    // field and applied ahead of the profile's own pipeline.
    //
    // An array of entries is passed straight through. A single entry keeps the older
    // behaviour exactly, including the quirk that `profileName` is merged INTO that one
    // entry rather than sent alongside it - every screen that passes an object also
    // leaves `profileName` empty, so the merge has never actually dropped anything, and
    // this is not the change to find out otherwise.
    buildInputs() {
      const dynamic = this.dynamicUpdateInputs;
      if (Array.isArray(dynamic)) {
        const entries = dynamic.filter((entry) => entry && Object.keys(entry).length);
        if (!entries.length) return [];
        return this.profileName !== ''
          ? [{ type: 'constant', prop: 'profileName', value: this.profileName }, ...entries]
          : entries;
      }
      if (!dynamic || !Object.keys(dynamic).length) return [];
      const profileNameNotExits = this.profileName !== ''
        ? { type: 'constant', prop: 'profileName', value: this.profileName }
        : {};
      return [{ ...profileNameNotExits, ...dynamic }];
    },
    async takeDbBackup() {
      this.importing = true;
      this.statusText = 'Loading file...';
      let successfulBackup = false;
      if (window.confirm('Would you like to take a backup before bulk changes?')) {
        this.setGroup('tmc-local-backend');
        this.setPrefix(`importfilesprebackup_${this.pipeline.program}`);
        const collection = importFileProgramMapperWithBackup[this.pipeline.program];
        // if mapper will not match with any program, it will get 'undefined'
        this.setCollection(collection);
        successfulBackup = await this.backup();
      }
      if (!successfulBackup) {
        this.importing = false;
        this.statusText = '';
        this.file = null;
      }
      return successfulBackup;
    },
    // The in-flight flag is raised here, synchronously at click time, so a second click cannot
    // start another run while the file is being fetched, backed up, validated or imported.
    async importFile() {
      if (this.isImportRunning) return false;
      this.isImportRunning = true;
      try {
        return await this.runImport();
      } finally {
        this.isImportRunning = false;
      }
    },
    async runImport() {
      // A locally picked file wins; otherwise ask the parent for one (Bill Requests
      // fetches the selected FTP file here rather than on selection). Deliberately
      // before the backup prompt, so a failed fetch cannot trigger a pointless backup.
      let { file } = this;
      let fromResolver = false;
      if (!file && this.fileResolver) {
        this.disableButtons = true;
        try {
          file = await this.fileResolver();
        } finally {
          this.disableButtons = false;
        }
        fromResolver = true;
      }

      // Nothing picked locally and nothing the resolver could supply. Said out loud
      // rather than returning quietly: an Import that does nothing at all reads as a
      // dead button, with no way to tell it apart from a failed request.
      if (!file) {
        this.flashInfo('Please choose a file to import, or select one from the file list.');
        return false;
      }

      if (file) {
        try {
          this.timing = new Date();

          // backup the db
          // A file that came from the resolver (i.e. pulled off FTP) skips the backup
          // outright - no prompt, no dump. Scoped to that path rather than to the whole
          // screen, so a file chosen with the file input keeps the safety net it has today.
          if (!this.isBackupNeeded && !fromResolver) {
            const successfulBackup = await this.takeDbBackup();
            // if backup failed, return
            if (!successfulBackup) return false;
          } // if backup is not needed, continue

          this.disableButtons = true;
          const inputsStr = JSON.stringify(this.buildInputs());

          // import the file
          const form = new FormData();
          form.append('fileName', sanitizeFilename(file.name));
          form.append('transMapName', this.pipeline.name);
          form.append('inputs', inputsStr);
          form.append('data', file);
          form.append('preValidate', true);
          this.chunkID = generateChunckID({ profileName: this.pipeline.name });
          form.append('chunkID', this.chunkID);
          form.append('profileID', this.pipeline._id);
          form.append('batchSize', this.pipeline.batchSize);

          const formHeaders = {
            'Content-Type': 'multipart/form-data',
          };
          [this.dialog, this.errors, this.errorMode] = await filePreValidate({ form, formHeaders });
          if (this.dialog) {
            this.form = form;
            return false;
          }

          this.disableButtons = false;
          // awaited so isImportRunning holds the Import button until the real import returns
          await this.insertUploadedFile(form);
          return true;
        } catch (err) {
          this.disableButtons = false;
          console.error(err);
          this.flashError('There was a problem while importing your file.');
          return false;
        }
      }
      return false;
    },
    dynamicReportUrl() {
      const programWiseSettings = {
        warSettings: this.warSettingItem,
      };
      const redirectionUrl = getImportRedirectionUrl(this.pipeline,
        this.programSelect, programWiseSettings);
      const parsedInputs = JSON.parse(this.pipeline.inputs || '[]');
      const route = generateReportUrl(
        redirectionUrl,
        parsedInputs,
        {
          chunkID: this.chunkID,
          program: this.pipeline.program,
        },
      );
      return route;
    },
    hasReportTemplate() {
      return (this.pipeline.reportTemplate || '').length > 0;
    },
    // openIfReportTemplate() {
    // if (this.hasReportTemplate()) {
    //   const toUrl = this.dynamicReportUrl();
    //   window.open(toUrl, '_blank');
    // }
    // },
    reloadMe() {
      Object.assign(this.$data, initdata());
      this.reloadKey += 1;
    },
  },
  watch: {
    makeBackupToTrue(val) {
      this.isBackupNeeded = val;
    },
    // So a screen can tell whether a local file is waiting, which it otherwise cannot
    // see - the file input lives in here. Bill Requests uses it to keep Import closed
    // until there is a file from somewhere, local or FTP.
    // Array-tolerant: v-file-input hands back a File in this component's usage, but some
    // Vuetify versions hand back an array, and an empty array is truthy.
    file(val) {
      this.$emit('fileChanged', Array.isArray(val) ? val.length > 0 : Boolean(val));
    },
  },
};
</script>
